import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import {
  ArrowRight, Play, Check, X, Zap, Box, IndianRupee,
  ShieldCheck, BarChart3, CreditCard, Home as HomeIcon, Sparkles,
  ChevronDown, ChevronUp, Star, PenTool, Shuffle, Users,
  FileWarning, GitMerge, AlertTriangle, Lock, ClipboardCheck,
  Clock, Target, TrendingDown, Landmark
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import AnimatedSection from '../components/AnimatedSection';
import './Home.css';

/* ───── TYPEWRITER ───── */
function TypewriterText({ texts, className }) {
  const [index, setIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = texts[index];
    let timeout;
    if (!deleting && displayed.length < current.length) {
      timeout = setTimeout(() => setDisplayed(current.slice(0, displayed.length + 1)), 60);
    } else if (!deleting && displayed.length === current.length) {
      timeout = setTimeout(() => setDeleting(true), 2000);
    } else if (deleting && displayed.length > 0) {
      timeout = setTimeout(() => setDisplayed(current.slice(0, displayed.length - 1)), 30);
    } else {
      setDeleting(false);
      setIndex((i) => (i + 1) % texts.length);
    }
    return () => clearTimeout(timeout);
  }, [displayed, deleting, index, texts]);

  return (
    <span className={className}>
      {displayed}
      <span className="typewriter-cursor">|</span>
    </span>
  );
}

/* ───── STAT COUNTER ───── */
function StatCard({ icon, end, suffix, label }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  return (
    <div ref={ref} className="stat-card glass-card">
      <div className="stat-card__icon">{icon}</div>
      <div className="stat-card__value">
        {inView ? <CountUp end={end} duration={2.5} separator="," /> : '0'}
        {suffix}
      </div>
      <div className="stat-card__label">{label}</div>
    </div>
  );
}

/* ───── FAQ ITEM ───── */
function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`}>
      <button className="faq-item__q" onClick={() => setOpen(!open)}>
        <span>{question}</span>
        {open ? <ChevronUp /> : <ChevronDown />}
      </button>
      <motion.div
        className="faq-item__a"
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <p>{answer}</p>
      </motion.div>
    </div>
  );
}

/* ───── PAIN POINT CARD ───── */
const painPoints = [
  {
    icon: <div className="icon-wrap icon-wrap--coral"><IndianRupee /></div>,
    title: 'The Budget Lie',
    desc: 'You start with ₹40 lakhs, end up at ₹55 lakhs. Nobody tells you about hidden costs - site development, finishes, boring wells, drainage. A builder quotes ₹1,500/sq ft but the real cost becomes ₹2,200/sq ft.',
  },
  {
    icon: <div className="icon-wrap icon-wrap--amber"><PenTool /></div>,
    title: 'Design Chaos',
    desc: 'You change the kitchen layout mid-construction - it costs ₹2 lakhs and delays the project by 3 weeks. Your architect and contractor fight about what the plan means. Nobody showed you what the house would look like before building.',
  },
  {
    icon: <div className="icon-wrap icon-wrap--coral"><Shuffle /></div>,
    title: 'Contractor Roulette',
    desc: 'You picked the cheapest contractor at ₹1,800/sq ft instead of ₹2,000/sq ft. He uses cheap materials, cuts corners, works slowly, keeps asking for more money. You end up paying ₹2,100/sq ft with bad quality.',
  },
  {
    icon: <div className="icon-wrap icon-wrap--amber"><Users /></div>,
    title: 'Worker Shortage',
    desc: 'India needs 2 million skilled workers and doesn\'t have them. A mason who charged ₹600/day now charges ₹1,000. Workers are untrained - your concrete is weak, waterproofing fails, wiring is wrong.',
  },
  {
    icon: <div className="icon-wrap icon-wrap--coral"><FileWarning /></div>,
    title: 'Approval Nightmares',
    desc: 'Official timeline says 30 days, reality is 2-4 months. 66% of under-construction buildings in Bangalore lack required approvals. You\'re stuck between government offices for months.',
  },
  {
    icon: <div className="icon-wrap icon-wrap--amber"><GitMerge /></div>,
    title: 'Coordination Collapse',
    desc: 'Architect, structural engineer, contractor, electrician, and plumber all disagree. By the time everyone aligns, one month is lost. Nobody takes responsibility.',
  },
  {
    icon: <div className="icon-wrap icon-wrap--coral"><AlertTriangle /></div>,
    title: 'Payment Risk',
    desc: 'You pay a large amount upfront, the contractor disappears or slows down. Your money is gone and the house is half-built. No accountability, no recourse.',
  },
  {
    icon: <div className="icon-wrap icon-wrap--amber"><Landmark /></div>,
    title: 'Home Loan Stress',
    desc: 'Banks reject your loan because the builder isn\'t verified or documentation is incomplete. You spend months running between lenders, gathering paperwork, and dealing with hidden processing fees - all while construction waits.',
  },
];

/* ───── SOLUTIONS ───── */
const solutions = [
  {
    icon: <Box />,
    title: 'AI-Powered 3D Design Studio',
    desc: 'Design your entire house by simply chatting with your AI architect - via text or voice, as easy as using WhatsApp. See it in live 3D, walk through it virtually, convert existing blueprints into 3D models. The platform enforces complete plans so nothing goes to construction half-baked.',
    img: '/screenshots/design-studio.png',
  },
  {
    icon: <IndianRupee />,
    title: 'Instant Real-Time Cost Estimation',
    desc: 'Every change you make instantly updates your total budget with full itemization - materials, labor, timeline. No more discovering the real cost after construction begins.',
    img: '/screenshots/Realtime-cost-estimator.PNG',
  },
  {
    icon: <ShieldCheck />,
    title: 'Verified Construction Partners',
    desc: 'Sculpit removes contractor selection entirely. Auto-assigns the best verified partner based on your project needs. All partners are RERA registered with 35+ years experience and ISO quality certified.',
    img: '/screenshots/verified-construction-partners.png',
  },
  {
    icon: <BarChart3 />,
    title: 'Real-Time Project Tracking',
    desc: 'Milestone dashboards, predictive delay alerts 3-5 days in advance, mandatory quality inspections at every critical stage - from pre-concrete to finishes to MEP testing.',
    img: '/screenshots/realtime-tracking.png',
  },
  {
    icon: <CreditCard />,
    title: 'Escrow-Protected Payments',
    desc: 'Money released only when milestones are verified. No upfront bulk payments, no contractor abandonment risk. Your money is safe until the work is done and inspected.',
    img: '/screenshots/escrow-payments.jpg',
  },
  {
    icon: <HomeIcon />,
    title: 'Home Loan Integration',
    desc: 'Pre-loan affordability analysis, lender-ready documentation, pre-verified builder credentials. Approval timelines cut from months to weeks.',
    img: '/screenshots/loans.png',
  },
  {
    icon: <Sparkles />,
    title: 'AI Interior Design',
    desc: 'AI-powered suggestions, 3D visualization of furnished rooms, curated material recommendations. See your interiors before spending a rupee.',
    img: '/screenshots/ai-interior-design.jpeg',
  },
];

/* ───── HOW IT WORKS STEPS ───── */
const steps = [
  {
    num: '01',
    title: 'Design Your Home',
    desc: 'Enter your plot size, start designing with the AI 3D studio. See your house come alive, get instant cost estimates. Takes just 15 minutes.',
    icon: <Box />,
  },
  {
    num: '02',
    title: 'Finalize Your Plan',
    desc: 'Compare designs, get bill of quantities, MEP planning with AI clash detection, Vastu compliance check, downloadable construction-ready blueprints.',
    icon: <Zap />,
  },
  {
    num: '03',
    title: 'Build with Confidence',
    desc: 'Sculpit assigns a verified partner. Milestone-based escrow payments, real-time tracking, mandatory quality inspections, all approvals handled.',
    icon: <ShieldCheck />,
  },
  {
    num: '04',
    title: 'Move In',
    desc: 'Utility connections handled, final inspections done, occupancy certificate obtained, interiors ready. Welcome home.',
    icon: <HomeIcon />,
  },
];

/* ───── COMPARISON ───── */
const comparisonRows = [
  { feature: 'Budget Clarity', traditional: 'Vague verbal estimates', sculpit: 'Instant AI-powered itemized costs' },
  { feature: 'Cost Overruns', traditional: '30-40% average overrun', sculpit: 'Real-time budget tracking, no surprises' },
  { feature: 'Contractor Reliability', traditional: 'Word-of-mouth gamble', sculpit: 'Verified, RERA-registered partners' },
  { feature: 'Design Changes', traditional: 'Expensive & chaotic mid-build', sculpit: 'Unlimited 3D changes before construction' },
  { feature: 'Schedule Tracking', traditional: 'Call contractor, hope for updates', sculpit: 'Live dashboard with predictive alerts' },
  { feature: 'Quality Control', traditional: 'Self-supervision required', sculpit: '15+ mandatory inspections' },
  { feature: 'Payment Safety', traditional: 'Large upfront payments, no recourse', sculpit: 'Milestone-based escrow protection' },
  { feature: 'Coordination', traditional: '6+ vendors, you manage all', sculpit: 'Single platform, one point of contact' },
];

/* ───── PRICING ───── */
const pricingPlans = [
  {
    name: 'Free',
    price: '₹0',
    period: 'forever',
    desc: 'Get started and explore',
    features: ['20 credits on signup', 'Create 2D floor plan', '2-3 designs in 3D', 'One cost breakdown', 'One free consultation', 'Browse 100+ templates'],
    cta: 'Start Free',
    highlighted: false,
  },
  {
    name: 'Pro',
    price: '₹2,999',
    period: '/month',
    desc: 'For serious home builders',
    badge: 'Most Popular',
    features: ['300 credits/month', 'Unlimited 3D views', '10-20+ design variations', 'Virtual 360° walkthroughs', 'Detailed itemized costs', 'MEP planning with clash detection', 'Downloadable blueprints', 'Vastu checker', '2 architect video calls', '10% construction discount', 'Priority support'],
    cta: 'Get Pro',
    highlighted: true,
  },
  {
    name: 'Business',
    price: '₹19,999',
    period: '/month',
    desc: 'For developers & builders',
    features: ['2,000 credits/month', 'Everything in Pro', 'Unlimited team members', 'White-label option', 'API access', 'Dedicated account manager', '15% construction discount'],
    cta: 'Contact Sales',
    highlighted: false,
  },
];

/* ───── FAQs ───── */
const faqs = [
  { q: 'What is Sculpit?', a: 'Sculpit is India\'s first AI-powered end-to-end residential construction platform. We help you design your home in 3D, get instant cost estimates, connect with verified builders, and track your entire construction - all from one place.' },
  { q: 'Is the 3D design tool really free?', a: 'Yes! You get 20 free credits on signup, which is enough to create your first floor plan, see it in 3D, and get a cost breakdown. No credit card needed.' },
  { q: 'How does escrow-protected payment work?', a: 'Your construction payments go into a Sculpit escrow account. Money is released to the builder only after each construction milestone is verified through quality inspections. This protects you from upfront payment risks.' },
  { q: 'Which cities does Sculpit operate in?', a: 'We currently serve homeowners building in Bangalore, Chennai, Hyderabad, and Pune. We\'re expanding to more cities soon.' },
  { q: 'Can I upload my existing blueprint?', a: 'Absolutely! Our AI can convert your existing 2D blueprint into an interactive 3D model. You can then modify it, see cost estimates, and make changes before construction starts.' },
  { q: 'How are construction partners vetted?', a: 'All Sculpit partners must be RERA registered, have 35+ years of combined team experience, ISO quality certification, and pass our rigorous onboarding process including site visits and reference checks.' },
];

/* ───── TEAM ───── */
const team = [
  { name: 'Abhishek Madeshia', role: 'Founder & CEO', bio: 'Founder of TechYzer, Dwibe & Co-Founder of LYFAUX Technology. Deep expertise in web, app, blockchain & AR/VR development.', img: '/screenshots/Abhishek.jpg' },
  { name: 'Seshu Kumar', role: 'Founder & COO', bio: 'Founder of LYFAUX Technology & Co-Founder of Dwibe. Specializes in operations, execution & organizational scaling.', img: '/screenshots/Seshu.jpg' },
  { name: 'Yashas', role: 'VP, Marketing & Growth', bio: 'Growth leader with experience in real estate tech & performance marketing at Google. Led go-to-market across high-growth startups.', img: '/screenshots/Yashas.jpeg' },
];

/* ═══════════════════════════════════════ */
/*                HOME PAGE               */
/* ═══════════════════════════════════════ */
export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <div className="home">
      {/* ═══ HERO ═══ */}
      <section className="hero" ref={heroRef}>
        <div className="hero__bg">
          <div className="hero__gradient" />
          <div className="hero__grid-pattern" />
          <div className="hero__orb hero__orb--1" />
          <div className="hero__orb hero__orb--2" />
        </div>

        <motion.div className="hero__content container" style={{ y: heroY, opacity: heroOpacity }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="label label-teal">
              <Sparkles /> AI-Powered Home Building
            </span>
          </motion.div>

          <motion.h1
            className="hero__title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <TypewriterText
              texts={[
                'Design Your Dream Home in 3D With Sculpit.',
                'Know the Exact Cost Instantly.',
                'Build with Complete Confidence.',
              ]}
              className="hero__typewriter"
            />
          </motion.h1>

          <motion.p
            className="hero__subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            India's first AI-powered construction platform. Design, plan, and build your home - all in one place. No surprises. No stress.
          </motion.p>

          <motion.div
            className="hero__buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <Link to="/pricing" className="btn btn-primary btn-lg">
              Start Designing Free <ArrowRight />
            </Link>
            <button className="btn btn-secondary btn-lg">
              <Play /> Watch How It Works
            </button>
          </motion.div>

          <motion.div
            className="hero__trust"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
          >
            <div className="hero__avatars">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="hero__avatar" style={{ background: `hsl(${i * 60}, 60%, 70%)` }}>
                  {String.fromCharCode(64 + i)}
                </div>
              ))}
            </div>
            <div className="hero__trust-text">
              <div className="hero__stars">
                {[1,2,3,4,5].map(i => <Star key={i} className="hero__star" fill="#F59E0B" />)}
              </div>
              <span>Trusted by 15,000+ homeowners</span>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero__mockup"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="hero__mockup-frame glass-card">
            <video
              src="/screenshots/hero-video.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="hero__video"
            />
          </div>
        </motion.div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <StatCard icon={<div className="icon-wrap icon-wrap--teal"><Clock /></div>} end={100} suffix="%" label="On Time House Construction" />
            <StatCard icon={<div className="icon-wrap icon-wrap--coral"><TrendingDown /></div>} end={40} suffix="%" label="Cost Overruns Eliminated" />
            <StatCard icon={<div className="icon-wrap icon-wrap--blue"><ClipboardCheck /></div>} end={15} suffix="+" label="Quality Inspections" />
            <StatCard icon={<div className="icon-wrap icon-wrap--purple"><Target /></div>} end={1} suffix="" label="Platform, Zero Stress" />
          </div>
        </div>
      </section>

      {/* ═══ PAIN POINTS ═══ */}
      <section className="pain-section section">
        <div className="pain-section__bg" />
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label" style={{ background: 'rgba(255,107,107,0.1)', color: 'var(--coral)', border: '1px solid rgba(255,107,107,0.2)' }}>
                The Reality Check
              </span>
              <h2>Building a House in India Shouldn't Feel Like a Gamble</h2>
              <p>These are real problems faced by lakhs of Indian families every year. Sound familiar?</p>
            </div>
          </AnimatedSection>

          <div className="pain-carousel">
            {painPoints.map((point, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="pain-card card-premium card-premium--coral">
                  {point.icon}
                  <h4 className="pain-card__title">{point.title}</h4>
                  <p className="pain-card__desc">{point.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection>
            <div className="pain-cta">
              <p className="pain-cta__text">
                Sound familiar? <strong>You're not alone.</strong> But it doesn't have to be this way.
              </p>
              <div className="pain-cta__arrow">↓</div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══ SOLUTIONS ═══ */}
      <section className="solutions-section section">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">
                <Zap /> The Sculpit Way
              </span>
              <h2>Here's How Sculpit <span className="text-teal">Changes Everything</span></h2>
              <p>Every feature is built to solve a real problem that Indian homeowners face during construction.</p>
            </div>
          </AnimatedSection>

          <div className="solutions-list">
            {solutions.map((sol, i) => (
              <AnimatedSection key={i} direction={i % 2 === 0 ? 'left' : 'right'}>
                <div className={`solution-row ${i % 2 !== 0 ? 'solution-row--reverse' : ''}`}>
                  <div className="solution-row__content">
                    <div className="icon-wrap icon-wrap--teal">{sol.icon}</div>
                    <h3>{sol.title}</h3>
                    <p>{sol.desc}</p>
                    <Link to="/features" className="solution-row__link">
                      Learn more <ArrowRight />
                    </Link>
                  </div>
                  <div className="solution-row__visual">
                    <div className="solution-row__img-wrap glass-card">
                      <img src={sol.img} alt={sol.title} />
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="hiw-section section section-gray">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Simple as 1-2-3-4</span>
              <h2>How It Works</h2>
              <p>From first sketch to moving in - Sculpit guides you through every step.</p>
            </div>
          </AnimatedSection>

          <div className="hiw-timeline">
            <div className="hiw-timeline__line" />
            {steps.map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div className="hiw-step">
                  <div className="hiw-step__marker">
                    <span className="hiw-step__num">{step.num}</span>
                  </div>
                  <div className="hiw-step__card card-premium">
                    <div className="hiw-step__icon">{step.icon}</div>
                    <h4>{step.title}</h4>
                    <p>{step.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection>
            <div style={{ textAlign: 'center', marginTop: '48px' }}>
              <Link to="/how-it-works" className="btn btn-primary btn-lg">
                See Detailed Walkthrough <ArrowRight />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══ COMPARISON ═══ */}
      <section className="comparison-section section">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Why Switch?</span>
              <h2>Traditional Construction vs. <span className="text-teal">Sculpit</span></h2>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="comparison-table-wrap glass-card">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className="comparison-table__old">Traditional</th>
                    <th className="comparison-table__new">Sculpit</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, i) => (
                    <tr key={i}>
                      <td className="comparison-table__feature">{row.feature}</td>
                      <td>
                        <span className="comparison-table__old-cell">
                          <X size={16} className="comparison-x" /> {row.traditional}
                        </span>
                      </td>
                      <td>
                        <span className="comparison-table__new-cell">
                          <Check size={16} className="comparison-check" /> {row.sculpit}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section className="pricing-section section section-gray">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Transparent Pricing</span>
              <h2>Start Free. Upgrade When You're Ready.</h2>
              <p>No hidden fees. No surprises. Just honest pricing for honest home building.</p>
            </div>
          </AnimatedSection>

          <div className="pricing-grid">
            {pricingPlans.map((plan, i) => (
              <AnimatedSection key={i} delay={i * 0.12}>
                <div className={`pricing-card card-premium ${plan.highlighted ? 'pricing-card--popular' : ''}`}>
                  {plan.badge && <div className="pricing-card__badge">{plan.badge}</div>}
                  <h3 className="pricing-card__name">{plan.name}</h3>
                  <p className="pricing-card__desc">{plan.desc}</p>
                  <div className="pricing-card__price">
                    <span className="pricing-card__amount">{plan.price}</span>
                    <span className="pricing-card__period">{plan.period}</span>
                  </div>
                  <ul className="pricing-card__features">
                    {plan.features.map((f, j) => (
                      <li key={j}><Check /> {f}</li>
                    ))}
                  </ul>
                  <Link
                    to="/pricing"
                    className={`btn ${plan.highlighted ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ width: '100%' }}
                  >
                    {plan.cta} <ArrowRight />
                  </Link>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection>
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <Link to="/pricing" className="solution-row__link" style={{ fontSize: '1rem' }}>
                See full pricing comparison & credit calculator <ArrowRight />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══ TRUST ═══ */}
      <section className="trust-section section">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Why Trust Sculpit?</span>
              <h2>Built by People Who Understand Construction</h2>
            </div>
          </AnimatedSection>

          <div className="trust-grid">
            <AnimatedSection delay={0}>
              <div className="trust-card card-premium">
                <div className="icon-wrap icon-wrap--teal icon-wrap--lg"><Users /></div>
                <h4>Founding Team</h4>
                <p>Serial entrepreneurs with deep expertise in technology platforms, AR/VR, operations, and real estate tech.</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="trust-card card-premium">
                <div className="icon-wrap icon-wrap--blue icon-wrap--lg"><ShieldCheck /></div>
                <h4>Verified Partners</h4>
                <p>Every construction partner is RERA registered, ISO quality certified, with 35+ years of combined team experience.</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="trust-card card-premium">
                <div className="icon-wrap icon-wrap--purple icon-wrap--lg"><Lock /></div>
                <h4>Protected Payments</h4>
                <p>Escrow-protected funds with milestone-based disbursement. Your money is safe until the work is verified.</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.3}>
              <div className="trust-card card-premium">
                <div className="icon-wrap icon-wrap--teal icon-wrap--lg"><ClipboardCheck /></div>
                <h4>15+ Inspections</h4>
                <p>Mandatory quality inspections at every critical stage - pre-concrete, structural, MEP, waterproofing, and finishes.</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="faq-section section section-gray">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Got Questions?</span>
              <h2>Frequently Asked Questions</h2>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="faq-list">
              {faqs.map((faq, i) => (
                <FAQItem key={i} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <Link to="/faq" className="btn btn-secondary">
                View All FAQs <ArrowRight />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="final-cta section-dark">
        <div className="final-cta__bg">
          <div className="final-cta__orb final-cta__orb--1" />
          <div className="final-cta__orb final-cta__orb--2" />
          <div className="final-cta__grid" />
        </div>
        <div className="container final-cta__content">
          <AnimatedSection>
            <h2 className="final-cta__title">
              Your Dream Home Is Waiting.<br />
              <span className="text-gradient">Let's Build It Together.</span>
            </h2>
            <p className="final-cta__subtitle">
              Start designing for free. No credit card needed. See your home in 3D in under 15 minutes.
            </p>
            <div className="final-cta__buttons">
              <Link to="/pricing" className="btn btn-primary btn-lg">
                Start Designing Now - It's Free <ArrowRight />
              </Link>
              <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="btn btn-outline-white btn-lg">
                <FaWhatsapp /> Chat on WhatsApp
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
